/**
 * Competitive seasons: the ladder, its reward tiers, and the two irreversible
 * buttons that start and finish one.
 *
 * A season is not editable once it is running — activating pays nothing but
 * ending PAYS OUT every tier, so both go through a confirm and neither is
 * offered on a season in the wrong state. The tier table is built row by row
 * because the reward ladder is the season: a season with no tiers ends by paying
 * nobody, which is a silent failure at the exact moment it matters.
 */
import { el, clear, toast } from '../../dom';
import { adminApi, type AdminSeason, type AdminSeasonTier } from '../../game-api';
import { fmtDateTime, fmtNum, localDateTimeValue } from './format';
import {
  apiMessage, btn, confirmDialog, emptyBox, errorBox, input, labelled, modal, panel, pill,
  spinner, table, toastError,
} from './ui';

const TONE: Record<string, 'ok' | 'warn' | 'bad' | 'mute'> = {
  ACTIVE: 'ok', UPCOMING: 'warn', ENDED: 'mute',
};


export function renderSeasons(host: HTMLElement): () => void {
  let cancelled = false;
  const listHost = el('div', 'min-h-[80px]');

  async function load() {
    clear(listHost);
    listHost.appendChild(spinner());
    try {
      const seasons = await adminApi.seasons();
      if (cancelled) return;
      clear(listHost);
      listHost.appendChild(draw(seasons));
    } catch (e) {
      if (cancelled) return;
      clear(listHost);
      listHost.appendChild(errorBox(apiMessage(e, "Couldn't load seasons."), () => void load()));
    }
  }

  function draw(seasons: AdminSeason[]): HTMLElement {
    if (!seasons.length) return emptyBox('No seasons yet. Create one to open a ladder.');
    return table(
      ['Name', 'Status', 'Runs', 'Tiers', ''],
      seasons.map((s) => [
        el('div', 'min-w-0', {},
          el('div', 'text-sm truncate', {}, s.name),
          el('div', 'text-[11px] text-text-dim', {}, `#${s.number}`)),
        pill(s.status, TONE[s.status] ?? 'mute'),
        `${fmtDateTime(s.startsAt)} → ${fmtDateTime(s.endsAt)}`,
        fmtNum(s.rewards?.length ?? 0),
        el('div', 'flex gap-1.5 flex-wrap', {},
          s.status === 'UPCOMING' ? btn('Activate', () => void activate(s), 'primary') : null,
          s.status === 'ACTIVE' ? btn('End & pay out', () => void end(s), 'danger') : null,
          // Only an UPCOMING season may be deleted — an ended one is a financial
          // record of rewards that were paid, and the API refuses it anyway.
          s.status === 'UPCOMING' ? btn('Delete', () => void remove(s), 'danger') : null),
      ]));
  }

  async function activate(s: AdminSeason) {
    const ok = await confirmDialog('Activate season',
      `Start "${s.name}"? Any other running season is closed first.`, 'Activate');
    if (!ok) return;
    try { await adminApi.activateSeason(s.id); toast('Season activated'); void load(); }
    catch (e) { toastError(e, 'Could not activate the season'); }
  }

  async function end(s: AdminSeason) {
    const ok = await confirmDialog('End season',
      `Finish "${s.name}" and pay out every tier reward? This cannot be undone.`, 'End & pay out');
    if (!ok) return;
    try { await adminApi.endSeason(s.id); toast('Season ended and rewards paid'); void load(); }
    catch (e) { toastError(e, 'Could not end the season'); }
  }

  async function remove(s: AdminSeason) {
    const ok = await confirmDialog('Delete season', `Delete "${s.name}"? This cannot be undone.`, 'Delete');
    if (!ok) return;
    try { await adminApi.removeSeason(s.id); toast('Season deleted'); void load(); }
    catch (e) { toastError(e, 'Could not delete the season'); }
  }

  function openForm() {
    const name = input('Season name');
    const now = new Date();
    const later = new Date(now.getTime() + 30 * 24 * 3600 * 1000);
    const startsAt = el('input',
      'w-full px-2.5 py-1.5 rounded-lg bg-surface-2 border border-border text-sm text-text outline-none focus:border-gold',
      { type: 'datetime-local', value: localDateTimeValue(now) }) as HTMLInputElement;
    const endsAt = el('input',
      'w-full px-2.5 py-1.5 rounded-lg bg-surface-2 border border-border text-sm text-text outline-none focus:border-gold',
      { type: 'datetime-local', value: localDateTimeValue(later) }) as HTMLInputElement;

    const tierRows: Array<{ row: HTMLElement; read: () => AdminSeasonTier }> = [];
    const tiersHost = el('div', 'flex flex-col gap-2');

    const addTier = (t?: Partial<AdminSeasonTier>) => {
      const tierName = input('Tier name', t?.tierName ?? '');
      const minPoints = input('0', String(t?.minPoints ?? 0), 'number');
      const soft = input('0', String(t?.rewardSoft ?? 0), 'number');
      const premium = input('0', String(t?.rewardPremium ?? 0), 'number');
      const xp = input('0', String(t?.rewardXp ?? 0), 'number');
      const row = el('div', 'grid gap-2 items-end',
        { style: 'grid-template-columns: repeat(auto-fit, minmax(90px, 1fr)) auto;' },
        labelled('Tier', tierName), labelled('Min points', minPoints),
        labelled('Coins', soft), labelled('Shrooms', premium), labelled('XP', xp),
        btn('×', () => {
          const i = tierRows.findIndex((r) => r.row === row);
          if (i >= 0) tierRows.splice(i, 1);
          row.remove();
        }, 'danger'));
      tierRows.push({
        row,
        read: () => ({
          tierName: tierName.value.trim(),
          minPoints: Number(minPoints.value) || 0,
          rewardSoft: Number(soft.value) || 0,
          rewardPremium: Number(premium.value) || 0,
          rewardXp: Number(xp.value) || 0,
        }),
      });
      tiersHost.appendChild(row);
    };
    addTier({ tierName: 'Bronze', minPoints: 0 });

    const save = btn('Create season', async () => {
      const tiers = tierRows.map((r) => r.read()).filter((t) => t.tierName);
      if (!name.value.trim()) { toast('A season needs a name', 'error'); return; }
      if (!tiers.length) { toast('Add at least one reward tier', 'error'); return; }
      if (new Date(endsAt.value) <= new Date(startsAt.value)) {
        toast('The season must end after it starts', 'error'); return;
      }
      try {
        await adminApi.createSeason({
          name: name.value.trim(),
          startsAt: new Date(startsAt.value).toISOString(),
          endsAt: new Date(endsAt.value).toISOString(),
          tiers,
        });
        toast('Season created');
        close();
        void load();
      } catch (e) { toastError(e, 'Could not create the season'); }
    }, 'primary');

    const close = modal('New season',
      el('div', 'flex flex-col gap-3', {},
        labelled('Name', name),
        el('div', 'grid gap-3', { style: 'grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));' },
          labelled('Starts', startsAt), labelled('Ends', endsAt)),
        el('div', '', {},
          el('div', 'text-[10px] uppercase tracking-widest text-text-dim mb-1', {}, 'Reward tiers'),
          tiersHost,
          el('div', 'mt-2', {}, btn('Add tier', () => addTier())))),
      [btn('Cancel', () => close()), save]);
  }

  host.appendChild(panel('Seasons',
    el('div', 'flex justify-between items-center gap-2 mb-3 flex-wrap', {},
      el('div', 'text-xs text-text-dim', {},
        'Ending a season pays out every tier reward — there is no undo.'),
      btn('New season', () => openForm(), 'primary')),
    listHost));
  void load();
  return () => { cancelled = true; };
}
